'use client';

import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MedScoutMapsProvider } from 'src/components/Maps/MedScoutMapsProvider';
import {
  useGetEngageMapMembersQuery,
  useGetEngageStrategyDetailsQuery,
} from 'src/api/redux/strategy/engageStrategyApi';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import {
  StrategyProfileMarker,
  StrategyResultsPanel,
  ChildRenderProps,
  StrategyProfileMapContent,
  StrategyProfileCircleContent,
  MapZoomControls,
} from './components';
import { StrategyExecutingState } from '../StrategyExecutingState';
import { StrategyProfileHeader } from '../StrategyProfileHeader';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useLogging } from 'src/context/logging';
import {
  AddToListModal,
  CircularProgress,
} from 'src/components';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { useScoutContext, SCOUT_CONTEXT_KEYS } from 'src/hooks/scout';
import { buildMapViewContext } from 'src/components/MedScoutAI/utils/pageContext/mapView';
import useProfileStrategies from 'src/hooks/strategy/useProfileStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { isStrategyExecutingErrors } from '../../utils';
import { extractErrorMessages } from '../../utils/errorUtils';
import { extractProviderLocation } from '../../utils/locationUtils';
import { StrategyErrorState } from '../StrategyErrorState';
import { StrategyLoadingState } from '../StrategyLoadingState';
import { useParams } from 'next/navigation';

interface StrategyProfileMapProps {
  engageStrategyId: string;
  children?: (props: ChildRenderProps) => ReactNode;
}

const StrategyProfileMap: React.FC<StrategyProfileMapProps> = ({
  engageStrategyId,
  children,
}) => {
  const { id } = useParams<{ id: string }>();
  const log = useLogging();

  // Dialog state for Add to List modal
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);

  // No-op function for view switching (map view is fixed in this component)
  const handleSetView = useCallback(() => {
    // No-op: Map view is the only view in this component
  }, []);

  const { entityDetails } = useProfile();

  // Fetch engage strategy details to get direction (inbound vs outbound)
  const { currentData: engageStrategyDetails } =
    useGetEngageStrategyDetailsQuery(engageStrategyId, {
      skip: !engageStrategyId,
    });

  const {
    search,
    sortValues,
    bounds,
    selectedSpecialties,
    selectedStatuses,
    selectedProviderLists,
    activeMarker,
    setActiveMarker,
    selectedProvider,
    setSelectedProvider,
    selectedProviders,
    clearSelectedProviders,
    zoom,
    setZoom,
    center,
    setCenter,
    setBounds,
    showHeatmap,
    selectedHeatmapMetric,
  } = useProfileStrategies();

  // Share what the map shows with Scout while it's on screen. Rounded and
  // debounced (compared by value) so panning and hovering don't re-register
  // on every frame.
  const scoutMapView = useMemo(
    () =>
      buildMapViewContext({
        zoom,
        bounds,
        activeMarker,
        heatmapOn: !!showHeatmap,
        heatmapMetric: selectedHeatmapMetric,
      }),
    [zoom, bounds, activeMarker, showHeatmap, selectedHeatmapMetric]
  );
  const [debouncedScoutMapView] = useDebounce(scoutMapView, 500, {
    equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });
  // Keyed per strategy so two maps on one profile don't remove each other.
  useScoutContext(
    `${SCOUT_CONTEXT_KEYS.PROFILE_STRATEGY_MAP}:${engageStrategyId}`,
    'Strategy map',
    { ...debouncedScoutMapView }
  );

  const handleOpenAddToList = useCallback(() => {
    setShowAddToListDialog(true);
  }, []);

  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number; targetListName: string }) => {
      log.event('Providers added to list from map marker', {
        listName: result.targetListName,
        count: result.addedCount,
      });
      clearSelectedProviders();
    },
    [log, clearSelectedProviders]
  );

  const handleDialogClose = useCallback(() => {
    setShowAddToListDialog(false);
    clearSelectedProviders();
  }, [clearSelectedProviders]);

  const { closeDrawer } = useDrawer();

  const isNotReferring = engageStrategyDetails?.type === 'NOT_REFERRING';

  // Clear selected provider and close both drawers when component unmounts
  useEffect(() => {
    return () => {
      setSelectedProvider(null);
      closeDrawer('profile');
    };
  }, []);

  // Convert bounds for API query
  const apiQueryBounds = useMemo(() => {
    if (!bounds || (bounds.min_lat === 0 && bounds.max_lat === 0)) return null;
    return {
      north: bounds.max_lat,
      south: bounds.min_lat,
      east: bounds.max_lng,
      west: bounds.min_lng,
    };
  }, [bounds]);

  // Shared filter parameters for map endpoint
  const filterParams = {
    strategyId: engageStrategyId,
    provider_id: id,
    q: search,
    order: sortValues.order,
    sort: sortValues.sort,
    ...(sortValues.sort_metric && { sort_metric: sortValues.sort_metric }),
    bounds: apiQueryBounds,
    specialties: selectedSpecialties,
    statuses: selectedStatuses,
    provider_lists: selectedProviderLists,
  };

  // Map endpoint - for markers
  const {
    data: engageMapMembers,
    isFetching: isMapFetching,
    isError: isMapError,
    error: mapError,
  } = useGetEngageMapMembersQuery(filterParams, {
    skip: !engageStrategyId || !id,
  });

  // Get source provider location from profile data
  const sourceProviderLocation = useMemo(
    () => extractProviderLocation(entityDetails),
    [entityDetails]
  );

  // Center map on source provider when location becomes available
  useEffect(() => {
    if (sourceProviderLocation) {
      setCenter(sourceProviderLocation);
    }
  }, [sourceProviderLocation, setCenter]);

  // Handle camera changes (zoom, center, bounds) - update Redux immediately
  const handleCameraChanged = useCallback(
    (event: any) => {
      if (!event?.detail) return;

      const {
        center: newCenter,
        zoom: newZoom,
        bounds: newBounds,
      } = event.detail;

      // Update Redux immediately (no debounce) so controlled props work
      if (newZoom !== undefined) {
        setZoom(newZoom);
      }

      if (newCenter?.lat !== undefined && newCenter?.lng !== undefined) {
        setCenter(newCenter);
      }

      if (newBounds) {
        const { north, south, east, west } = newBounds;
        if (north && south && east && west) {
          setBounds({ north, south, east, west });
        }
      }
    },
    [setZoom, setCenter, setBounds]
  );

  // Handle bounds changes for API queries (debounced) - updates Redux
  const handleApiQueryBoundsChanged = useCallback(
    (boundsEvent: any) => {
      if (!boundsEvent) return;

      if (!boundsEvent?.detail?.bounds) return;

      const { north, south, east, west } = boundsEvent.detail.bounds;
      if (!north || !south || !east || !west) return;

      setBounds({ north, south, east, west });
    },
    [setBounds]
  );

  const debouncedApiQueryBoundsChanged = useDebouncedCallback(
    handleApiQueryBoundsChanged,
    500
  );

  // Show loading state while waiting for initial map data
  if (isMapFetching && !engageMapMembers) {
    return <StrategyLoadingState height="h-[640px]" />;
  }

  // Show error state if map data fetch failed
  if (isMapError) {
    // Check if strategy is still executing
    if (isStrategyExecutingErrors([mapError])) {
      return (
        <StrategyExecutingState
          className="w-full"
          height="h-[640px]"
          header={
            <StrategyProfileHeader
              view="map"
              setView={handleSetView}
              description={engageStrategyDetails?.description || ''}
              title={engageStrategyDetails?.label || ''}
            />
          }
        />
      );
    }

    // Show error state for other errors
    const errorMessage = extractErrorMessages(
      [mapError],
      'An error occurred while loading opportunities. If this issue persists, please contact your Customer Success team member for assistance.'
    );

    return (
      <StrategyErrorState
        title="Error Loading Opportunities"
        message={errorMessage}
        height="h-[640px]"
      />
    );
  }

  return (
    <div className="w-full h-160 overflow-hidden pb-2">
      <div className="w-full h-full overflow-hidden rounded-lg border border-neutral-200">
        <div
          data-testid="strategy-profile-map"
          className="flex items-center w-full h-full bg-slate-100"
        >
          <MedScoutMapsProvider>
            <div className="relative w-full h-full rounded-bl-lg overflow-hidden">
              <Map
                mapId="strategy-profile-map"
                className="w-full h-full"
                disableDefaultUI
                gestureHandling={'greedy'}
                center={center}
                zoom={zoom}
                minZoom={4}
                maxZoom={15}
                onCameraChanged={handleCameraChanged}
                onBoundsChanged={debouncedApiQueryBoundsChanged}
                scrollwheel
                zoomControl={false}
                zoomControlOptions={undefined}
              >
                {/* Source Provider Marker with faStar icon in blue circle */}
                {sourceProviderLocation && (
                  <AdvancedMarker
                    position={sourceProviderLocation}
                    title="Source Provider"
                    zIndex={1000}
                  >
                    <div
                      className="w-8 h-8 rounded-full bg-sky-600 
                      border-4 border-white flex items-center justify-center flex-shrink-0"
                    >
                      <Icon icon={faStar} className="text-white" />
                    </div>
                  </AdvancedMarker>
                )}

                {/* Engage Map Members Markers */}
                {engageMapMembers &&
                  !showHeatmap &&
                  Array.isArray(engageMapMembers) &&
                  engageMapMembers.map((member: any) => {
                    if (!member.lat || !member.lng) return null;
                    return (
                      <StrategyProfileMarker
                        key={member.id || member.provider_id}
                        listMember={member}
                        position={{ lat: member.lat, lng: member.lng }}
                        isHovered={
                          activeMarker === member.provider_id?.toString()
                        }
                        onMarkerClick={(memberId) =>
                          setActiveMarker(
                            activeMarker === memberId ? null : memberId
                          )
                        }
                        onMouseOver={(memberId) => setActiveMarker(memberId)}
                        onMouseOut={() => setActiveMarker(null)}
                        onAddToList={handleOpenAddToList}
                      />
                    );
                  })}

                {/* Map Content - handles bounds fitting on initial load */}
                <StrategyProfileMapContent
                  engageStrategyId={engageStrategyId}
                />

                {/* Circle Content - renders radius circle for NOT_REFERRING strategies */}
                {isNotReferring && (
                  <StrategyProfileCircleContent
                    center={sourceProviderLocation}
                    radius={(engageStrategyDetails?.radius ?? 0) * 1609.34}
                    isVisible={isNotReferring}
                  />
                )}

                {/* Map Controls */}
                <MapZoomControls engagedId={engageStrategyId} />
              </Map>

              {/* Loading Indicator */}
              {isMapFetching && (
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-3 shadow-lg z-[1000]">
                  <div className="flex items-center space-x-2">
                    <CircularProgress size="sm" color="primary" />
                    <span className="text-sm font-medium text-gray-700">
                      Loading strategy data...
                    </span>
                  </div>
                </div>
              )}
            </div>
          </MedScoutMapsProvider>

          {/* Results Sidebar */}
          <StrategyResultsPanel
            engageStrategyId={engageStrategyId}
            setShowAddToListDialog={setShowAddToListDialog}
          >
            {children}
          </StrategyResultsPanel>
        </div>
        <AddToListModal
          isOpen={showAddToListDialog}
          onClose={handleDialogClose}
          mode="add"
          providers={selectedProviders}
          onSuccess={handleAddToListSuccess}
        />
      </div>
    </div>
  );
};

export default StrategyProfileMap;
